// Locale-dependent formatting (dates, currency) differs between server and
// client and will cause hydration mismatches if rendered during SSR. This
// composable gates such values: it returns a stable placeholder until the
// component has mounted, then the client fills in the locally-formatted value.
export function useClientFormat() {
  const mounted = ref(false)
  onMounted(() => { mounted.value = true })

  function fmtDateTime(iso?: string | null, fallback = '—') {
    if (!mounted.value || !iso) return fallback
    const d = new Date(iso)
    if (isNaN(d.getTime())) return fallback
    return d.toLocaleString(undefined, {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    })
  }

  function fmtMoney(n?: number | null, fallback = '—') {
    if (!mounted.value || n === null || n === undefined) return fallback
    return n.toLocaleString(undefined, { maximumFractionDigits: 0 })
  }

  function fmtNum(n?: number | null, dp = 2, fallback = '—') {
    if (n === null || n === undefined) return fallback
    return n.toFixed(dp)
  }

  // USD (real money) is integer CENTS end-to-end — distinct from fmtMoney, which
  // formats whole BMO points. Same client-only gate to avoid an SSR/locale
  // hydration mismatch. currency is a param so a non-USD wallet stays correct.
  function fmtUsdCents(cents?: number | null, currency = 'USD', fallback = '—') {
    if (!mounted.value || cents === null || cents === undefined) return fallback
    // narrowSymbol: non-US locales otherwise disambiguate to "US$10.00"
    return (cents / 100).toLocaleString(undefined, {
      style: 'currency', currency, currencyDisplay: 'narrowSymbol',
    })
  }

  return { mounted, fmtDateTime, fmtMoney, fmtNum, fmtUsdCents }
}
